import React, { useState } from 'react';
import { ChevronLeft, Edit2, X, MapPin, Clock, DollarSign, Users, Calendar as CalendarIcon } from 'lucide-react';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { CardSkeleton } from '../common/Skeleton';
import EmptyState from '../common/EmptyState';
import ConfirmModal from '../common/ConfirmModal';
import SwipeableItem from '../common/SwipeableItem';
import ShowDetailModal from '../common/ShowDetailModal';
import TimelineEditModal from '../common/TimelineEditModal';
import toast from 'react-hot-toast';

// Helper function to convert 24-hour time to 12-hour format
const formatTime12Hour = (time) => {
  if (!time) return '';
  
  // Handle if time is already in a format like "7:00 PM"
  if (time.includes('AM') || time.includes('PM')) return time;
  
  // Parse the time string (expects "HH:MM" or "H:MM")
  const [hours, minutes] = time.split(':').map(Number);
  
  if (isNaN(hours) || isNaN(minutes)) return time;
  
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12; // Convert 0 to 12 for midnight
  
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

const Timeline = ({ shows, onNavigate, onDeleteShow, onEditShow, onUpdateShow, loading, salesLog = [], documents = [] }) => {
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [selectedShow, setSelectedShow] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingShow, setEditingShow] = useState(null);

  // Convert date-keyed structure to flat array
  const getAllShows = () => {
    const allShows = [];
    
    if (!shows || typeof shows !== 'object') {
      return [];
    }

    // Loop through each date key (e.g., "2024-11-25")
    Object.keys(shows).forEach(dateKey => {
      const showsOnDate = shows[dateKey];
      
      // Each date has an array of shows
      if (Array.isArray(showsOnDate)) {
        showsOnDate.forEach(show => {
          // Filter out "other" events - only include actual shows
          if (show.eventType !== 'other') {
            allShows.push({
              ...show,
              date: dateKey, // Store the date string
              dateKey: dateKey // Keep for deletion and updates
            });
          }
        });
      }
    });

    return allShows;
  };

  // Get all shows and sort by date
  const sortedShows = getAllShows().sort((a, b) => new Date(a.date) - new Date(b.date));

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const upcomingShows = sortedShows.filter(show => new Date(show.date) >= today);
  const pastShows = sortedShows.filter(show => new Date(show.date) < today).reverse();

  const handleShowClick = (show) => {
    setSelectedShow(show);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedShow(null);
  };

  // Edit Modal Handlers
  const handleEditShow = (show) => {
    setEditingShow(show);
    setEditModalOpen(true);
  };

  const handleModalUpdate = (showId, dateKey, updatedData) => {
    onUpdateShow(showId, dateKey, updatedData);
    setEditModalOpen(false);
    setEditingShow(null);
  };

  const handleModalClose = () => {
    setEditModalOpen(false);
    setEditingShow(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
        </div>
      </div>
    );
  }

  const ShowCard = ({ show, isPast }) => (
    <SwipeableItem
      onEdit={() => handleEditShow(show)}
      onDelete={() => setConfirmDelete(show)}
    >
      <div
        onClick={() => handleShowClick(show)}
        className={`w-full text-left p-4 rounded-lg border-l-4 ${
          isPast 
            ? 'bg-gray-50 dark:bg-gray-900 border-gray-400 dark:border-gray-600' 
            : 'bg-blue-50 dark:bg-blue-900/20 border-blue-500'
        } hover:shadow-md transition-all cursor-pointer`}
      >
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
          <div className="flex-1">
            <h3 className={`font-bold text-lg mb-2 ${
              isPast 
                ? 'text-gray-600 dark:text-gray-400' 
                : 'text-gray-800 dark:text-gray-100'
            }`}>
              {show.venue || show.title || 'Untitled Show'}
            </h3>
            
            <div className="space-y-1 text-sm">
              {show.venue && (
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                  <MapPin size={16} />
                  <span>{show.venue}{show.city && `, ${show.city}`}</span>
                </div>
              )}
              
              {show.loadInTime && (
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                  <Clock size={16} />
                  <span>Load-in: {formatTime12Hour(show.loadInTime)}</span>
                </div>
              )}
              
              {show.showTime && (
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                  <Clock size={16} />
                  <span>Show: {formatTime12Hour(show.showTime)}</span>
                </div>
              )}
              
              {show.guarantee && (
                <div className="flex items-center gap-2 text-green-600 dark:text-green-400 font-semibold">
                  <DollarSign size={16} />
                  <span>Guarantee: {formatCurrency(parseFloat(show.guarantee))}</span>
                </div>
              )}

              {show.attendance && (
                <div className="flex items-center gap-2 text-purple-600 dark:text-purple-400">
                  <Users size={16} />
                  <span>{show.attendance} attendance</span>
                </div>
              )}
            </div>

            {show.showNotes && (
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 italic">
                📝 {show.showNotes}
              </p>
            )}
          </div>

          <div className="flex flex-col items-end gap-2">
            <span className={`text-sm font-semibold ${
              isPast 
                ? 'text-gray-500 dark:text-gray-400' 
                : 'text-blue-600 dark:text-blue-400'
            }`}>
              {formatDate(show.date)}
            </span>
            
            {/* Desktop action buttons */}
            <div className="hidden md:flex gap-2" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditShow(show);
                }}
                className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded transition-colors"
                title="Edit Show"
              >
                <Edit2 size={16} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmDelete(show);
                }}
                className="p-2 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors"
                title="Delete Show"
              >
                <X size={16} />
              </button>
            </div>

            {/* Mobile swipe hint */}
            <span className="md:hidden text-xs text-gray-400 dark:text-gray-500">
              ← Swipe left
            </span>
            
            {/* Click to view hint */}
            <span className="text-xs text-blue-600 dark:text-blue-400 hover:underline">
              Click for details →
            </span>
          </div>
        </div>
      </div>
    </SwipeableItem>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 pb-24 md:pb-8">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-30">
        {/* Mobile Header */}
        <div className="md:hidden flex items-center justify-between px-4 py-3">
          <button 
            onClick={() => onNavigate('dashboard')} 
            className="p-2 rounded-full bg-gray-100 dark:bg-gray-700"
          >
            <ChevronLeft size={24} />
          </button>
          <h1 className="text-lg font-bold text-gray-800 dark:text-gray-100">Show Timeline</h1>
          <button
            onClick={() => onNavigate('calendar')}
            className="p-2 rounded-full bg-purple-600 text-white"
          >
            <CalendarIcon size={20} />
          </button>
        </div>

        {/* Desktop Header */}
        <div className="hidden md:block max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl lg:text-2xl font-bold text-gray-800 dark:text-gray-100">Show Timeline</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">View all your shows</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => onNavigate('calendar')}
                className="flex items-center gap-2 px-4 py-2.5 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition-colors"
              >
                <CalendarIcon size={20} />
                Calendar
              </button>
              <button 
                onClick={() => onNavigate('dashboard')} 
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Summary Stats */}
        {sortedShows.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4 rounded-xl">
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Shows</p>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{sortedShows.length}</p>
            </div>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4 rounded-xl">
              <p className="text-sm text-gray-600 dark:text-gray-400">Upcoming</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">{upcomingShows.length}</p>
            </div>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4 rounded-xl">
              <p className="text-sm text-gray-600 dark:text-gray-400">Past Shows</p>
              <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{pastShows.length}</p>
            </div>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4 rounded-xl">
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Guarantee</p>
              <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                {formatCurrency(sortedShows.reduce((sum, show) => sum + (parseFloat(show.guarantee) || 0), 0))}
              </p>
            </div>
          </div>
        )}

        {sortedShows.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
            <EmptyState
              icon={CalendarIcon}
              title="No Shows Scheduled"
              description="Add your first show."
              action={() => onNavigate('calendar')}
              actionLabel="Add Show"
            />
          </div>
        ) : (
          <>
            {/* Upcoming Shows */}
            {upcomingShows.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
                  <span className="text-2xl">📅</span>
                  Upcoming Shows ({upcomingShows.length})
                </h2>
                <div className="space-y-3">
                  {upcomingShows.map(show => (
                    <ShowCard key={`${show.dateKey}-${show.id}`} show={show} isPast={false} />
                  ))}
                </div>
              </div>
            )}

            {/* Past Shows */}
            {pastShows.length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
                  <span className="text-2xl">🕐</span>
                  Past Shows ({pastShows.length})
                </h2>
                <div className="space-y-3">
                  {pastShows.map(show => (
                    <ShowCard key={`${show.dateKey}-${show.id}`} show={show} isPast={true} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Confirm Delete Modal */}
      <ConfirmModal
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => {
          onDeleteShow(confirmDelete.dateKey, confirmDelete.id);
          setConfirmDelete(null);
          toast.success('Show deleted');
        }}
        title="Delete Show?"
        message={`Are you sure you want to delete "${confirmDelete?.venue || confirmDelete?.title}" on ${confirmDelete?.date}?`}
        danger={true}
      />

      {/* Show Detail Modal */}
      <ShowDetailModal
        show={selectedShow}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onUpdate={onUpdateShow}
        salesLog={salesLog}
        onNavigate={onNavigate}
        documents={documents}
      />

      {/* Timeline Edit Modal */}
      <TimelineEditModal
        show={editingShow}
        isOpen={editModalOpen}
        onClose={handleModalClose}
        onUpdate={handleModalUpdate}
      />
    </div>
  );
};

export default Timeline;